import { API_URL, extension_id } from '../utils/api';
import { collection, getDocs, query, where, orderBy, doc, getDoc, updateDoc, limit } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

class OrderApis {
  constructor(storeId, userId, email) {
    this.storeId = storeId;
    this.userId = userId; // Add userId to track the dealer user ID
    this.email = email;
  }

  // Get draft orders (customer carts)
  async getDraftOrders() {
    try {
      console.log('🔍 OrderApis.getDraftOrders() - Fetching customer carts for user:', this.userId);
      
      const response = await fetch(`${API_URL}/customers/${this.userId}/carts?extensionId=${extension_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('❌ OrderApis.getDraftOrders() - API call failed:', response.status);
        return [];
      }

      const data = await response.json();
      const carts = data.customerCarts || [];
      
      console.log('✅ OrderApis.getDraftOrders() - Successfully fetched', carts.length, 'draft orders');
      return carts.map(cart => ({
        id: cart.id,
        customerId: cart.customer_id,
        status: cart.status || 'draft',
        totalAmount: cart.total_amount || 0,
        createdAt: cart.created_at,
        tag: cart.tag || '',
        type: 'customer_cart'
      }));
    } catch (error) {
      console.error('❌ OrderApis.getDraftOrders() - Error fetching draft orders:', error);
      return [];
    }
  }

  // Get in processing orders (order carts)
  // These are carts that have been converted to order carts but not yet completed orders
  async getInProcessingOrders() {
    try {
      console.log('🔍 OrderApis.getInProcessingOrders() - Fetching order carts for user:', this.userId);
      
      // Note: This endpoint may need adjustment based on your actual order carts API
      // You may need to provide the correct endpoint for fetching order carts
      const response = await fetch(`${API_URL}/customers/${this.userId}/order-carts?extensionId=${extension_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('❌ OrderApis.getInProcessingOrders() - API call failed:', response.status);
        return [];
      }

      const data = await response.json();
      const orderCarts = data.orderCarts || data.carts || [];
      
      console.log('✅ OrderApis.getInProcessingOrders() - Successfully fetched', orderCarts.length, 'order carts');
      return orderCarts.map(cart => ({
        id: cart.id,
        customerId: cart.customer_id,
        status: cart.status || 'Processing',
        totalAmount: cart.total_amount || 0,
        createdAt: cart.created_at,
        tag: cart.tag || '',
        type: 'order_cart'
      }));
    } catch (error) {
      console.error('❌ OrderApis.getInProcessingOrders() - Error fetching order carts:', error);
      return [];
    }
  }

  // Get active orders (orders with status "Active") with pagination
  // Uses the sales-orders endpoint: orders made to this store, scoped by the
  // order's store + this dealer (works for both supplier and reseller stores).
  async getActiveOrders(page = 1, limit = 10) {
    try {
      const skip = (page - 1) * limit;
      console.log('🔍 OrderApis.getActiveOrders() - Fetching active orders for customer:', this.userId, 'page:', page);

      const response = await fetch(`${API_URL}/customers/${this.userId}/orders/sales?orderStatusName=Active&skip=${skip}&take=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('❌ OrderApis.getActiveOrders() - API call failed:', response.status);
        return { orders: [], total: 0 };
      }

      const data = await response.json();
      const orders = data.orders || [];
      const total = data.total || 0;

      console.log('✅ OrderApis.getActiveOrders() - Successfully fetched', orders.length, 'active orders, total:', total);
      return {
        orders: orders.map(order => ({
          id: order.id,
          customerId: order.customer?.id,
          customerName: order.customer ? `${order.customer.first_name} ${order.customer.last_name}`.trim() : 'Unknown',
          status: order.status,
          totalAmount: order.order_total || 0,
          createdAt: order.created_at,
          orderNumber: order.order_number || '',
          tag: order.order_tag || '',
          type: 'order'
        })),
        total: total
      };
    } catch (error) {
      console.error('❌ OrderApis.getActiveOrders() - Error fetching active orders:', error);
      return { orders: [], total: 0 };
    }
  }

  // Get completed orders (orders with status "Completed") with pagination
  // Uses the sales-orders endpoint: orders made to this store, scoped by the
  // order's store + this dealer (works for both supplier and reseller stores).
  async getCompletedOrders(page = 1, limit = 10) {
    try {
      const skip = (page - 1) * limit;
      console.log('🔍 OrderApis.getCompletedOrders() - Fetching completed orders for customer:', this.userId, 'page:', page);

      const response = await fetch(`${API_URL}/customers/${this.userId}/orders/sales?orderStatusName=Completed&skip=${skip}&take=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('❌ OrderApis.getCompletedOrders() - API call failed:', response.status);
        return { orders: [], total: 0 };
      }

      const data = await response.json();
      const orders = data.orders || [];
      const total = data.total || 0;

      console.log('✅ OrderApis.getCompletedOrders() - Successfully fetched', orders.length, 'completed orders, total:', total);
      return {
        orders: orders.map(order => ({
          id: order.id,
          customerId: order.customer?.id,
          customerName: order.customer ? `${order.customer.first_name} ${order.customer.last_name}`.trim() : 'Unknown',
          status: order.status,
          totalAmount: order.order_total || 0,
          createdAt: order.created_at,
          orderNumber: order.order_number || '',
          tag: order.order_tag || '',
          completedAt: order.updated_at,
          type: 'order'
        })),
        total: total
      };
    } catch (error) {
      console.error('❌ OrderApis.getCompletedOrders() - Error fetching completed orders:', error);
      return { orders: [], total: 0 };
    }
  }

  // Get all customer carts - using proper API endpoint
  async getCustomerCarts() {
    try {
      console.log('🔍 OrderApis.getCustomerCarts() - Fetching all carts for customer:', this.userId);
      
      const response = await fetch(`${API_URL}/customers/${this.userId}/carts?extensionId=${extension_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ OrderApis.getCustomerCarts() - Raw carts data:', data);
        
        if (data.success && data.customerCarts) {
          const carts = data.customerCarts.map(cart => ({
            id: cart.id,
            order_number: cart.id,
            customer_id: cart.customer_id,
            customer: cart.customer,
            status: cart.status || 'active',
            total_amount: cart.total_amount || 0,
            created_at: cart.created_at,
            items: cart.items || [],
            type: 'cart'
          }));
          
          console.log('✅ OrderApis.getCustomerCarts() - Successfully fetched', carts.length, 'carts');
          return carts;
        }
      } else {
        console.error('❌ OrderApis.getCustomerCarts() - API call failed:', response.status);
        const errorData = await response.json();
        console.error('Error details:', errorData);
      }
      
      return [];
      
    } catch (error) {
      console.error('❌ OrderApis.getCustomerCarts() - Error fetching customer carts:', error);
      return [];
    }
  }

  // Get a specific order by ID - Using correct customer orders endpoint
  async getOrderById(orderId) {
    try {
      console.log('🔍 OrderApis.getOrderById() - Fetching order:', orderId, 'for customer:', this.userId);
      
      // Use the correct customer orders endpoint
      const response = await fetch(`${API_URL}/customers/${this.userId}/orders/${orderId}?extensionId=${extension_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ OrderApis.getOrderById() - Raw order data:', data);
        
        if (data.success && data.order) {
          const rawOrder = data.order;
          
          // Transform the data to match the format expected by OrderDetails component
          const transformedOrder = {
            id: rawOrder.id,
            order_number: rawOrder.order_number,
            status: rawOrder.status?.status_name || rawOrder.status,
            total_amount: rawOrder.order_customer_total ? rawOrder.order_customer_total : rawOrder.order_total,
            created_at: rawOrder.created_at,
            order_customer_subtotal: rawOrder.order_customer_subtotal,
            order_customer_total: rawOrder.order_customer_total,
            order_customer_discount_rate: rawOrder.order_customer_discount_rate,
            order_customer_discount_type: rawOrder.order_customer_discount_type,
            order_customer_tax_rate: rawOrder.order_customer_tax_rate,
            customer: {
              id: rawOrder.customer?.id,
              first_name: rawOrder.customer?.name?.split(' ')[0] || rawOrder.customer?.name || '',
              last_name: rawOrder.customer?.name?.split(' ').slice(1).join(' ') || '',
              email: rawOrder.customer?.email,
              phone_number: rawOrder.customer?.phone,
              customer_location: rawOrder.customer?.address
            },
            items: rawOrder.order_items?.map(item => ({
              id: item.id,
              product: {
                id: item.product?.id,
                product_name: item.product?.name,
                sku: item.product?.sku,
                description: item.product?.description
              },
              quantity: item.quantity,
              price: item.customer_product_price,
              status: item.status,
              item_total: item.item_total,
              customer_item_total: item.customer_item_total,
              inputs: item.user_inputs?.reduce((acc, input) => {
                acc[input.input_name] = input.user_input + (input.measurement_unit ? ` ${input.measurement_unit}` : '');
                return acc;
              }, {}) || {},
              variations: item.variations || []
            })) || [],
            subtotal: rawOrder.order_customer_subtotal || rawOrder.order_total,
            discount: rawOrder.order_customer_discount_rate && rawOrder.order_customer_discount_rate !== '0' ? {
              discount_type: rawOrder.order_customer_discount_type,
              discount_rate: parseFloat(rawOrder.order_customer_discount_rate)
            } : null,
            tax: rawOrder.order_customer_tax_rate && rawOrder.order_customer_tax_rate !== '0' ? 
              parseFloat(rawOrder.order_customer_tax_rate) : null,
            tag: rawOrder.tag,
            statusGroup: rawOrder.statusGroup
          };
          
          console.log('✅ OrderApis.getOrderById() - Transformed order:', transformedOrder);
          return transformedOrder;
        } else if (data) {
          // Sometimes the order data might be directly in the response
          console.log('✅ OrderApis.getOrderById() - Order data found directly:', data);
          return data;
        }
      } else {
        // Log the error response for debugging
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ OrderApis.getOrderById() - API call failed:', response.status, errorData);
        
        if (response.status === 404) {
          console.log('❌ OrderApis.getOrderById() - Order not found (404)');
        }
      }
      
      console.log('❌ OrderApis.getOrderById() - Order not found:', orderId);
      return null;
      
    } catch (error) {
      console.error('❌ OrderApis.getOrderById() - Error fetching order:', error);
      return null;
    }
  }

  // Legacy method - keep for backward compatibility but now uses draft orders
  async getOrders() {
    return this.getDraftOrders();
  }

  // Get a specific order - now looks at saved estimates
  async getOrder(orderId) {
    try {
      console.log('🔍 OrderApis.getOrder() - Fetching order:', orderId);
      
      const estimateRef = doc(db, "dealer-portal-users",this.email, "estimates", orderId);
      const snapshot = await getDoc(estimateRef);

      if (!snapshot.exists()) {
        console.log('📝 OrderApis.getOrder() - Order not found');
        return null;
      }

      const data = snapshot.data();
      
      // Only return if it's actually an order (status: saved)
      if (data.status !== 'saved' || !data.isConvertedToOrder) {
        console.log('📝 OrderApis.getOrder() - Document is not an order');
        return null;
      }

      const order = {
        id: snapshot.id,
        estimateNo: data.estimateNo,
        customerID: data.customerID,
        userId: data.userId,
        customerName: data.customerName,
        cartID: data.cartID,
        orderID: data.orderID,
        estimate_profit: data.estimate_profit,
        estimateData: data.estimateData || {},
        createdAt: data.createdAt,
        validUntil: data.validUntil,
        isConvertedToOrder: data.isConvertedToOrder,
        status: data.status
      };

      console.log('✅ OrderApis.getOrder() - Successfully fetched order');
      return order;
    } catch (error) {
      console.error('❌ OrderApis.getOrder() - Error fetching order:', error);
      return null;
    }
  }

  // Create a new order - EXACTLY like expo app approach
  async createOrder(estimateData, estimateID) {
    if (!estimateID || !estimateData) {
      throw new Error("Invalid estimate data");
    }

    try {
      console.log('🔍 OrderApis.createOrder() - Creating order for estimate:', estimateID);
      console.log('🔍 OrderApis.createOrder() - User ID:', this.userId);
      console.log('🔍 OrderApis.createOrder() - Estimate data:', estimateData);
      
      if (!this.userId) {
        throw new Error('User ID not available in OrderApis');
      }
      
      const estimateRef = doc(db, "dealer-portal-users",this.email, "estimates", estimateID);
      console.log('🔍 OrderApis.createOrder() - Firestore path:', `dealer-portal-users/${this.userId}/estimates/${estimateID}`);
      
      // First check if the estimate exists and get the cart info
      const currentDoc = await getDoc(estimateRef);
      if (!currentDoc.exists()) {
        console.error('❌ OrderApis.createOrder() - Estimate not found!');
        throw new Error(`Estimate ${estimateID} not found`);
      }
      
      const estimateDoc = currentDoc.data();
      console.log('✅ OrderApis.createOrder() - Found estimate:', estimateDoc);
      
      const cartId = estimateDoc.cartID;
      const customerId = estimateDoc.customerID;
      
      if (!cartId || !customerId) {
        throw new Error('Missing cart ID or customer ID in estimate');
      }
      
      console.log('🔍 OrderApis.createOrder() - Converting cart to order cart via API...');
      console.log('🔍 OrderApis.createOrder() - Cart ID:', cartId, 'Customer ID:', customerId);
      
      // Step 1: Call API to convert cart to order cart (EXACTLY like expo app)
      const response = await fetch(`${API_URL}/customers/${customerId}/carts/${cartId}/convertOrderCart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extension_id: extension_id,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ OrderApis.createOrder() - API call failed:', response.status, errorText);
        throw new Error(`Failed to convert cart to order: ${response.status} ${errorText}`);
      }

      const orderData = await response.json();
      console.log('✅ OrderApis.createOrder() - Cart converted to order cart:', orderData);
      
      // Step 2: Update Firestore document (EXACTLY like expo app)
      const updateData = {
        isConvertedToOrder: true,
        status: "saved",
        orderID: orderData.orderCart.id, // This is crucial!
      };
      
      console.log('🔍 OrderApis.createOrder() - Updating Firestore with data:', updateData);
      
      await updateDoc(estimateRef, updateData);
      
      // Verify the update worked
      const verifyDoc = await getDoc(estimateRef);
      console.log('✅ OrderApis.createOrder() - Verified updated document:', verifyDoc.data());
      
      console.log('✅ OrderApis.createOrder() - Order created successfully');
      return estimateID;
    } catch (error) {
      console.error('❌ OrderApis.createOrder() - Error creating order:', error);
      throw new Error("Error creating order: " + error.message);
    }
  }

  // The remaining methods below are kept for potential future API integration,
  // but the core order functionality now uses the estimate-based approach

  // Update an order
  async updateOrder(orderId, orderData) {
    try {
      const response = await fetch(`${API_URL}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.order;
      } else {
        const error = await response.json();
        console.error('Error updating order:', error);
        throw new Error(error.message || 'Failed to update order');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  }

  // Delete an order
  async deleteOrder(orderId) {
    try {
      const response = await fetch(`${API_URL}/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return true;
      } else {
        const error = await response.json();
        console.error('Error deleting order:', error);
        throw new Error(error.message || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }

  // Update order status
  async updateOrderStatus(orderId, status) {
    try {
      const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.order;
      } else {
        const error = await response.json();
        console.error('Error updating order status:', error);
        throw new Error(error.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Get order statistics
  async getOrderStats() {
    try {
      const response = await fetch(`${API_URL}/orders/stats/${this.storeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.stats || {};
      } else {
        const error = await response.json();
        console.error('Error fetching order stats:', error);
        return {};
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
      return {};
    }
  }
}

export default OrderApis; 